import type { FaqContent, HeroContent, LinkItem } from './types';

export const investHero: HeroContent = {
  title: 'Invest in Stocks, ETFs,\nBonds & Gold Indices with nsave',
  cta: { label: 'Get the app', href: '/get-the-app' },
  image: {
    src: '/images/invest/hero.webp',
    alt: 'Investing in stocks, ETFs, bonds and gold indices with the nsave app',
  },
};

/** Full-bleed "Start with $1." lifestyle section shown above the showcase. */
export const investStart = {
  title: 'Start with $1.',
  body: 'nsave protects your earnings and helps you grow your wealth. All within one unified mobile app. Start your investment journey today with just $1 and begin building your future.',
  image: {
    src: '/images/invest/start.webp',
    alt: 'A person sitting on a bed checking their investments on the nsave app',
  },
  articles: [
    {
      label: 'How to Invest in Stocks',
      href: '/article/how-to-invest-in-us-stocks-with-nsave',
    },
    {
      label: 'How to Invest in Gold Indices',
      href: '/article/how-to-invest-in-gold-indices-with-nsave',
    },
  ] as LinkItem[],
};

/** Centered belief/mission statement shown between the hero and the showcase. */
export const investBelief = {
  lines: [
    'We believe that everyone deserves the opportunity to build up wealth.',
    "That's why we are making investing and saving easily accessible to everyone.",
  ],
};

export interface Instrument {
  name: string;
  symbol: string;
  /** Real instrument logo (scraped from the live site); omit to fall back to a monogram. */
  logo?: string;
}

const LOGO_DIR = '/images/invest/logos';

export const investShowcase: { title: string; subtitle: string; instruments: Instrument[] } = {
  title: '5000+ Stocks, ETFs..',
  // Explicit line breaks (\n) reproduce the reference's exact 3-line wrap on
  // desktop via `whitespace-pre-line`; on mobile the copy wraps naturally.
  subtitle:
    'Explore over 5000 U.S. stocks, ETFs, bonds and\ngold indices in your trusted nsave app. Start your\njourney with just $1.',
  instruments: [
    { name: 'SPDR S&P 500', symbol: 'SPY', logo: `${LOGO_DIR}/spy.png` },
    { name: 'SilverBox Corp', symbol: 'SBXD', logo: `${LOGO_DIR}/sbxd.png` },
    { name: 'UnitedHealth', symbol: 'UNH', logo: `${LOGO_DIR}/unh.png` },
    { name: 'Mastercard', symbol: 'MA', logo: `${LOGO_DIR}/ma.png` },
    { name: "McDonald's", symbol: 'MCD', logo: `${LOGO_DIR}/mcd.png` },
    { name: 'Walmart Inc', symbol: 'WMT', logo: `${LOGO_DIR}/wmt.png` },
    { name: 'JP Morgan Chase', symbol: 'JPM', logo: `${LOGO_DIR}/jpm.png` },
    { name: 'Berkshire Hathaway', symbol: 'BRK-B', logo: `${LOGO_DIR}/brk-b.png` },
    { name: 'HP Inc.', symbol: 'HPQ', logo: `${LOGO_DIR}/hpq.png` },
    { name: 'Nvidia Corp', symbol: 'NVDA', logo: `${LOGO_DIR}/nvda.png` },
    { name: 'Nike Inc.', symbol: 'NKE', logo: `${LOGO_DIR}/nke.png` },
    { name: 'Airbnb Inc.', symbol: 'ABNB', logo: `${LOGO_DIR}/abnb.png` },
    { name: 'Palantir Technologies', symbol: 'PLTR', logo: `${LOGO_DIR}/pltr.png` },
    { name: 'Shopify Inc.', symbol: 'SHOP', logo: `${LOGO_DIR}/shop.png` },
    { name: 'United Parcel Service', symbol: 'UPS', logo: `${LOGO_DIR}/ups.png` },
    { name: 'Adobe Inc.', symbol: 'ADBE', logo: `${LOGO_DIR}/adbe.png` },
    { name: 'Advanced Micro Devices', symbol: 'AMD', logo: `${LOGO_DIR}/amd.png` },
    { name: 'PepsiCo Inc.', symbol: 'PEP', logo: `${LOGO_DIR}/pep.png` },
    { name: 'Microsoft Corp', symbol: 'MSFT', logo: `${LOGO_DIR}/msft.png` },
    { name: 'Verizon Comms', symbol: 'VZ', logo: `${LOGO_DIR}/vz.png` },
    { name: 'iShares Gold', symbol: 'IAU', logo: `${LOGO_DIR}/iau.png` },
    { name: 'Alphabet Inc.', symbol: 'GOOGL', logo: `${LOGO_DIR}/googl.png` },
    { name: 'The Coca-Cola Co', symbol: 'KO', logo: `${LOGO_DIR}/ko.png` },
    { name: 'Bank of America', symbol: 'BAC', logo: `${LOGO_DIR}/bac.png` },
    { name: 'Netflix Inc.', symbol: 'NFLX', logo: `${LOGO_DIR}/nflx.png` },
    { name: 'Honeywell Intl', symbol: 'HON', logo: `${LOGO_DIR}/hon.png` },
    { name: 'Dell Technologies', symbol: 'DELL', logo: `${LOGO_DIR}/dell.png` },
    { name: 'Deere & Co', symbol: 'DE', logo: `${LOGO_DIR}/de.png` },
    { name: 'Pfizer Inc.', symbol: 'PFE', logo: `${LOGO_DIR}/pfe.png` },
    { name: 'Texas Instruments', symbol: 'TXN', logo: `${LOGO_DIR}/txn.png` },
    // No logo on the live site — falls back to a monogram.
    { name: 'abrdn Physical Silver', symbol: 'SIVR' },
    { name: 'abrdn Physical Gold', symbol: 'SGOL', logo: `${LOGO_DIR}/sgol.png` },
    { name: 'iShares Silver', symbol: 'SLV', logo: `${LOGO_DIR}/slv.png` },
    { name: 'Vanguard S&P 500', symbol: 'VOO', logo: `${LOGO_DIR}/voo.png` },
    { name: 'T. Rowe Price', symbol: 'TRBCX', logo: `${LOGO_DIR}/trbcx.png` },
    { name: 'Fidelity Contrafund', symbol: 'FCNTX', logo: `${LOGO_DIR}/fcntx.png` },
    { name: 'iShares S&P 500', symbol: 'IVV', logo: `${LOGO_DIR}/ivv.png` },
  ],
};

export const investFaq: FaqContent = {
  title: 'Frequently asked Questions',
  items: [
    {
      question: 'What are stocks, ETFs, bonds & gold indices?',
      answer:
        "We've put together a collection of articles that explain, step by step, what stocks, ETFs, and gold indices are and how they work. Each guide is designed to help you understand the basics of investing and show you how you can easily invest with nsave.",
    },
    {
      question: "What's the minimum amount I need to invest?",
      answer:
        "It's completely free to get started, and you can begin investing with as little as $1. From there, you can increase your investment amount whenever you like based on your goals and what feels right for you.",
    },
    {
      question: 'Do I need US citizenship?',
      answer:
        'No. You can invest with nsave without having US citizenship. The nsave app takes care of international transfers and complies with all relevant regulations.',
    },
  ],
};

/** Legal disclaimer shown at the bottom of the invest page, below the FAQ. */
export const investDisclaimer =
  'nwealth Inc. is a Delaware corporation that has applied to register as an investment adviser with the U.S. Securities and Exchange Commission ("SEC"). Brokerage and custody services will be provided by Alpaca Securities, LLC, an unaffiliated broker-dealer. Investing involves risk, including the possible loss of principal. Past performance is no guarantee of future results. Nothing on this page is investment, tax, or legal advice, or a recommendation to buy or sell any security.';
